import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ROLE_BY_KEY, ENTITY_HINT } from "@/lib/contacts";
import { fmtPhone } from "@/lib/format";
import { Loader2, UserRound } from "lucide-react";

const EMPTY = { entity: "", name: "", phone: "", email: "", contact_id: null };

function Suggestions({ list, onPick }) {
  if (!list.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 max-h-44 overflow-auto" data-testid="contact-suggestions">
      <div className="px-3 pt-2 text-[11px] uppercase tracking-[0.12em] text-slate-500">Já usados na agência</div>
      {list.map((c) => (
        <button key={c.id} type="button" onClick={() => onPick(c)} data-testid={`contact-suggestion-${c.id}`}
          className="w-full text-left px-3 py-2 hover:bg-white transition-colors flex items-start gap-2">
          <UserRound size={14} className="mt-0.5 text-slate-400 shrink-0" />
          <span className="text-sm text-ec-green">{[c.name, c.entity].filter(Boolean).join(" · ")}
            <span className="block text-xs text-slate-500">{[fmtPhone(c.phone), c.email].filter(Boolean).join(" · ")}</span></span>
        </button>
      ))}
    </div>
  );
}

export function ContactDialog({ pid, role, initial, note, onClose, onSaved }) {
  const r = ROLE_BY_KEY[role];
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!role) { return; }
    setF({ ...EMPTY, ...Object.fromEntries(Object.entries(initial || {}).filter(([k, v]) => k in EMPTY && v)) });
    setQ(""); setList([]);
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!role || q.trim().length < 2) { setList([]); return; }
    const t = setTimeout(() => {
      api.get("/contacts", { params: { type: r.type, q } }).then(({ data }) => setList(data.slice(0, 6))).catch(() => setList([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, role]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = (k) => (e) => { setF({ ...f, [k]: e.target.value, contact_id: null }); if (k === "entity" || k === "name") setQ(e.target.value); };
  const pick = (c) => { setF({ entity: c.entity || "", name: c.name || "", phone: fmtPhone(c.phone) || "", email: c.email || "", contact_id: c.id }); setList([]); setQ(""); };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/processes/${pid}/contacts/${role}`, f);
      toast.success("Contacto guardado"); onSaved(data.contact);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!role) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-ec-green">{r.label}</DialogTitle>
          <DialogDescription data-testid="contact-dialog-note">{note || "Comece a escrever a entidade ou o nome para ver contactos já usados."}</DialogDescription>
        </DialogHeader>
        <div><Label className="text-xs">Entidade</Label><Input value={f.entity} onChange={s("entity")} placeholder={ENTITY_HINT[r.type]} className="mt-1" data-testid="contact-entity" /></div>
        <div><Label className="text-xs">Nome</Label><Input value={f.name} onChange={s("name")} className="mt-1" data-testid="contact-name" /></div>
        <Suggestions list={list} onPick={pick} />
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Telefone</Label><Input value={f.phone} onChange={s("phone")} inputMode="tel" className="mt-1" data-testid="contact-phone" /></div>
          <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={s("email")} type="email" className="mt-1" data-testid="contact-email" /></div>
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={busy || !(f.name || f.entity)} data-testid="contact-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ec-green text-white font-semibold hover:bg-ec-greenDark transition-colors disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} Guardar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
